import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import axios from 'axios';
import { departmentsAPI, employeesAPI } from '../api/api';
import * as XLSX from 'xlsx';
import useCompanySettings from '../hooks/useCompanySettings';
import { pricingTable } from './Payment';
import { useNavigate } from 'react-router-dom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';

// Şirket ayarlarını çekmek için hook
function Employees() {
  const settings = useCompanySettings();
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]); // Departmanlar state'i
  const [shifts, setShifts] = useState([]); // Vardiyalar state'i
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    employee_id: '',
    first_name: '',
    last_name: '',
    department: '',
    position: '',
    email: '',
    phone: '',
    hire_date: '',
    shift: '',
    status: 'Active',
    base_salary: '',
    rfid_card_id: '',
  });
  const [editMode, setEditMode] = useState(false);
  const [rfidCards, setRfidCards] = useState([]);
  const [usedRfidCards, setUsedRfidCards] = useState([]);
  const [visitorRfidCards, setVisitorRfidCards] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');
  const [employeeLimit, setEmployeeLimit] = useState(null);
  const [selectedShift, setSelectedShift] = useState('');

  // Tarihi dd-MM-yyyy formatında gösteren yardımcı fonksiyon
  function formatHireDateTR(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  useEffect(() => {
    fetchEmployees();
    fetchRfidCards();
    fetchDepartments();
    fetchShifts();
    fetchEmployeeLimit();
    fetchVisitors();
  }, []);

  const fetchDepartments = async () => {
    try {
      console.log('Fetching departments...');
      const response = await departmentsAPI.getAll();
      console.log('Departments response:', response.data);
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setSnackbarMessage('Departmanlar yüklenemedi: ' + (error.response?.data?.error || error.message));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const fetchShifts = async () => {
    try {
      const response = await axios.get('https://pdks.bytebridge.com.tr/api/shifts');
      setShifts(response.data);
    } catch (error) {
      console.error('Error fetching shifts:', error);
    }
  };

  const fetchEmployeeLimit = async () => {
    try {
      const response = await axios.get('https://pdks.bytebridge.com.tr/api-validity?port=5001');
      const lim = response.data.employee_count || response.data.employeeCount || response.data.EmployeeCount || response.data[0]?.employee_count || 0;
      setEmployeeLimit(Number(lim));
    } catch (error) {
      console.error('Error fetching employee limit:', error);
      setEmployeeLimit(null);
    }
  };

  const fetchVisitors = async () => {
    try {
      const response = await axios.get('https://pdks.bytebridge.com.tr/api/visitors');
      setVisitorRfidCards(response.data.map(v => v.RfidCardID).filter(Boolean));
    } catch (error) {
      console.error('Error fetching visitors:', error);
    }
  };

  const columns = [
    { field: 'ID', headerName: 'ID', width: 70 },
    { field: 'FullName', headerName: 'Ad Soyad', flex: 1, valueGetter: (params) => `${params.row.FirstName || ''} ${params.row.LastName || ''}`.trim() },
    { field: 'Department', headerName: 'Departman', flex: 1 },
    { field: 'Position', headerName: 'Pozisyon', flex: 1 },
    { field: 'Email', headerName: 'E-posta', flex: 1 },
    { field: 'Phone', headerName: 'Telefon', flex: 1 },
    { field: 'HireDate', headerName: 'İşe Giriş Tarihi', flex: 1, valueGetter: (params) => formatHireDateTR(params.row.HireDate) },
    { field: 'Shift', headerName: 'Vardiya', flex: 1 },
    { field: 'Status', headerName: 'Durum', flex: 1 },
    { field: 'BaseSalary', headerName: 'Temel Maaş', flex: 1 },
    {
      field: 'actions',
      type: 'actions',
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem icon={<DeleteIcon />} label="Delete" onClick={() => handleDelete(params.row.ID)} />,
        <GridActionsCellItem icon={<EditIcon />} label="Edit" onClick={() => handleEdit(params.row)} />,
      ],
    },
  ];

  const navigate = useNavigate();

  const handleLimitWarningClick = () => {
    navigate('/payment');
  };

  const fetchEmployees = async () => {
    try {
      console.log('Fetching employees...');
      const response = await employeesAPI.getAll();
      console.log('Employees response:', response.data);
      
      // Check if response.data has employees property
      const employeesArray = response.data.employees || response.data;
      
      // Validate that we have an array
      if (!Array.isArray(employeesArray)) {
        console.error('API returned non-array data:', response.data);
        setEmployees([]);
        setLoading(false);
        return;
      }
      
      // Ad/soyad eksikse farklı key'lerden tamamla
      const normalized = employeesArray.map(emp => ({
        ...emp,
        FirstName: emp.FirstName || emp.first_name || emp.firstname || '',
        LastName: emp.LastName || emp.last_name || emp.lastname || '',
      }));
      setEmployees(normalized);
      setLoading(false);
      const usedIds = normalized.map((employee) => employee.EmployeeId); // Still use EmployeeId for RFID cards
      setUsedRfidCards(usedIds);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setSnackbarMessage('Çalışanlar yüklenemedi: ' + (error.response?.data?.error || error.message));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setLoading(false);
    }
  };

  const fetchRfidCards = async () => {
    try {
      const response = await axios.get('https://pdks.bytebridge.com.tr/api/rfidcards');
      const data = response.data;
      
      // Validate that data is an array
      if (!Array.isArray(data)) {
        console.error('RFID cards API returned non-array data:', data);
        setRfidCards([]);
        return;
      }
      
      setRfidCards(data);
    } catch (error) {
      console.error('Error fetching RFID cards:', error);
      setRfidCards([]);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setEditMode(false);
    setFormData(prev => ({
      ...prev,
      base_salary: settings?.baseSalaryDefault || '',
      department: '',
      shift: '',
      rfid_card_id: '',
    }));
    fetchShifts();
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (e.target.name === 'shift') setSelectedShift(e.target.value);
  };

  const handleDelete = async (employeeId) => {
    try {
      await employeesAPI.delete(employeeId);
      fetchEmployees();
      fetchRfidCards(); // Re-fetch RFID cards after deleting an employee
      setSnackbarMessage('Çalışan başarıyla silindi.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error deleting employee:', error);
      setSnackbarMessage('Çalışan silinemedi: ' + (error.response?.data?.error || error.message));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleEdit = (row) => {
    setFormData({
      employee_id: row.EmployeeId, // Use EmployeeId for the form field
      first_name: row.FirstName,
      last_name: row.LastName,
      department: row.Department,
      position: row.Position,
      email: row.Email,
      phone: row.Phone,
      hire_date: row.HireDate,
      shift: row.Shift || '',
      status: row.Status,
      base_salary: row.BaseSalary,
      rfid_card_id: row.RfidCardID,
    });
    setSelectedShift(row.Shift || '');
    setEditMode(true);
    setOpen(true);
    fetchShifts();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editMode && isEmployeeLimitExceeded()) {
      setSnackbarMessage('Çalışan limiti aşıldı. Lütfen paketinizi yükseltin.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    try {
      if (editMode) {
        await employeesAPI.update(formData.employee_id, formData);
        setSnackbarMessage('Çalışan başarıyla güncellendi.');
        setSnackbarSeverity('success');
      } else {
        await employeesAPI.create(formData);
        setSnackbarMessage('Çalışan başarıyla eklendi.');
        setSnackbarSeverity('success');
      }
      fetchEmployees();
      fetchRfidCards(); // Re-fetch RFID cards after adding/updating an employee
      handleClose();
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Bir hata oluştu.';
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const isEmployeeLimitExceeded = () => {
    if (employeeLimit == null || !employees) return false;
    return employees.length >= employeeLimit;
  };

  const availableRfidCards = rfidCards.filter(card => {
    const cardId = card.CardID || card.card_id || card.id;
    // Eğer edit modundaysak ve mevcut çalışanın kartı ise gösterilsin
    if (editMode && formData.rfid_card_id && String(formData.rfid_card_id) === String(cardId)) return true;
    return !usedRfidCards.includes(cardId) && !visitorRfidCards.includes(cardId);
  });

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleExportExcel = () => {
    if (!employees || employees.length === 0) {
      alert('Aktarılacak çalışan bulunamadı!');
      return;
    }
    const excelRows = [
      ['ID', 'Adı', 'Soyadı', 'Departman', 'Pozisyon', 'E-posta', 'Telefon', 'İşe Giriş Tarihi', 'Vardiya', 'Durum', 'Temel Maaş']
    ];
    employees.forEach(emp => {
      excelRows.push([
        emp.ID,
        emp.FirstName,
        emp.LastName,
        emp.Department,
        emp.Position,
        emp.Email,
        emp.Phone,
        formatHireDateTR(emp.HireDate),
        emp.Shift,
        emp.Status,
        emp.BaseSalary
      ]);
    });
    const ws = XLSX.utils.aoa_to_sheet(excelRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Çalışanlar');
    XLSX.writeFile(wb, `calisanlar_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  return (
    <Box sx={{ height: '80vh', width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <Tooltip title="Çalışanları bu sayfadan yönetebilirsiniz.">
          <InfoOutlinedIcon color="primary" style={{ marginRight: 8, cursor: 'pointer' }} />
        </Tooltip>
        <h2 style={{ margin: 0 }}>Çalışanlar</h2>
      </div>
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
          <Button variant="contained" color="primary" onClick={handleOpen}>
            Yeni Çalışan Ekle
          </Button>
          <Button variant="outlined" color="success" onClick={handleExportExcel}>
            Çalışanları Excel'e Aktar
          </Button>
        </Box>
      </Box>
      <Paper elevation={3}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={employees}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10]}
            checkboxSelection
            disableSelectionOnClick
            getRowId={(row) => row.ID}
            autoHeight
          />
        )}
      </Paper>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{editMode ? 'Edit Employee' : 'Add New Employee'}</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 1 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>RFID Kart ID</InputLabel>
              <Select
                label="RFID Kart ID"
                name="rfid_card_id"
                value={formData.rfid_card_id || ''}
                onChange={handleChange}
              >
                <MenuItem value=""><em>Seçiniz</em></MenuItem>
                {availableRfidCards.map(card => (
                  <MenuItem key={card.CardID || card.card_id || card.id} value={card.CardID || card.card_id || card.id}>
                    {card.CardID || card.card_id || card.id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              margin="normal"
              required
              fullWidth
              name="first_name"
              label="Adı"
              value={formData.first_name}
              onChange={handleChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="last_name"
              label="Soyadı"
              value={formData.last_name}
              onChange={handleChange}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel id="department-label">Departman</InputLabel>
              <Select
                labelId="department-label"
                name="department"
                value={formData.department}
                onChange={handleChange}
                required
              >
                {departments.map(dept => (
                  <MenuItem value={dept.DepartmentName} key={dept.ID}>{dept.DepartmentName}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              margin="normal"
              required
              fullWidth
              name="position"
              label="Pozisyon"
              value={formData.position}
              onChange={handleChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="email"
              label="E-posta"
              type="email"
              value={formData.email}
              onChange={handleChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="phone"
              label="Telefon"
              value={formData.phone}
              onChange={handleChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="hire_date"
              label="İşer Giriş Tarihi"
              type="date"
              value={formData.hire_date}
              onChange={handleChange}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel id="shift-label">Vardiya</InputLabel>
              <Select
                labelId="shift-label"
                name="shift"
                value={formData.shift || ''}
                onChange={handleChange}
                required
              >
                {shifts.map((shift) => (
                  <MenuItem value={shift.ShiftName} key={shift.ID}>{shift.ShiftName} ({shift.StartTime}-{shift.EndTime})</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              margin="normal"
              required
              fullWidth
              name="status"
              label="Durum"
              value={formData.status}
              onChange={handleChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="base_salary"
              label="Maaş"
              value={formData.base_salary}
              onChange={handleChange}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Vazgeç</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editMode ? 'Güncelle' : 'Ekle'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%', cursor: snackbarMessage.includes('limiti aşıldı') ? 'pointer' : 'default' }}
          onClick={snackbarMessage.includes('limiti aşıldı') ? handleLimitWarningClick : undefined}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Employees;